import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Bell, Settings as SettingsIcon, LogOut as LogOutIcon } from 'lucide-react'
import { useAuthStore } from '../../stores/auth'

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, isAuthenticated, logout } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    if (!isUserMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  const navigation = [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Messages', href: '/messages' },
    { name: 'Hub', href: '/hub' },
    // { name: 'Analytics', href: '/analytics' }, // Hidden for now
    // { name: 'Settings', href: '/settings' }, // Remove from here
  ]

  const isActive = (href: string) => location.pathname === href

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 mobile-safe-area">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Enhanced for mobile */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center touch-target">
              <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate-mobile">
                <span className="hidden xs:inline">Connect Within Reach</span>
                <span className="xs:hidden">CWR</span>
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          {isAuthenticated && (
            <nav className="hidden md:flex space-x-4 lg:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors touch-target-sm ${
                    isActive(item.href)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          )}

          {/* Right side - Enhanced for mobile */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {isAuthenticated ? (
              <>
                {/* Notifications Icon - Enhanced touch target */}
                <Link 
                  to="/notifications" 
                  className="btn-icon" 
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5 md:h-6 md:w-6 text-gray-500 hover:text-blue-600" />
                </Link>
                
                {/* User Menu - Enhanced for mobile */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    className="flex items-center space-x-2 touch-target rounded-lg hover:bg-gray-100"
                    onClick={() => setIsUserMenuOpen((open) => !open)}
                    aria-label="User menu"
                    aria-expanded={isUserMenuOpen}
                  >
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <span className="hidden md:block text-sm font-medium text-gray-700 max-w-24 truncate">
                      {user?.name}
                    </span>
                  </button>
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                      <div className="px-4 py-3 border-b border-gray-100 md:hidden">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.name || user?.email}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                      </div>
                      <Link
                        to="/settings"
                        className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center touch-target-sm"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <SettingsIcon className="h-4 w-4 mr-3 text-gray-500" />
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          logout()
                          setIsUserMenuOpen(false)
                        }}
                        className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center touch-target-sm"
                      >
                        <LogOutIcon className="h-4 w-4 mr-3 text-gray-500" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-secondary text-sm px-4 py-2">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm px-4 py-2">
                  Sign Up
                </Link>
              </>
            )}

            {/* Mobile menu button - Enhanced */}
            {isAuthenticated && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden btn-icon"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Enhanced Mobile Navigation */}
        {isMobileMenuOpen && isAuthenticated && (
          <div className="md:hidden border-t border-gray-200 pb-safe">
            <div className="px-2 pt-2 pb-3 space-y-1 animate-slide-down">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`mobile-menu-item ${
                    isActive(item.href)
                      ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile-only user info section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="px-4 py-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
                <Link
                  to="/settings"
                  className="mobile-menu-item text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <SettingsIcon className="h-5 w-5 mr-3 text-gray-500" />
                  Settings
                </Link>
                <button
                  onClick={() => {
                    logout()
                    setIsMobileMenuOpen(false)
                  }}
                  className="mobile-menu-item text-gray-500 hover:text-gray-900 hover:bg-gray-50 w-full text-left"
                >
                  <LogOutIcon className="h-5 w-5 mr-3 text-gray-500" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
} 